import { Banner, SpinnerWheel } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'

import { getRateFileAnalysis, type Proposal } from '../domain/Proposal'
import { useRfpContext } from './contexts/RfpContext'

type UploadLanesFileBannersProps = {
  proposal: Proposal
}

export function UploadLanesFileBanners({ proposal }: UploadLanesFileBannersProps) {
  const {
    state: { uploadLanesFileStatus },
    setUploadLanesFileStatus,
  } = useRfpContext()

  const { analyzingRateFile, rateFileAnalysisFail, rateFileAnalysisSuccess } =
    getRateFileAnalysis(proposal)

  function resetUploadLanesFileStatus() {
    /**
     * Status should be set to Initial
     * Otherwise, if carrier closes the banner, and upload the same file with no changes
     * No banner will be shown because status will be the same, and carrier will not have feedback
     */
    setUploadLanesFileStatus(RequestStatus.Initial)
  }

  if (uploadLanesFileStatus === RequestStatus.Error) {
    return (
      <Banner variant="danger" dismissible onDismiss={resetUploadLanesFileStatus}>
        <Banner.Title>Could not upload the file</Banner.Title>
        <Banner.Description>
          Make sure you are uploading your prices using the template with no changes on the
          spreadsheet headers.
        </Banner.Description>
      </Banner>
    )
  }

  if (analyzingRateFile) {
    return (
      <Banner>
        <Banner.Icon>
          <SpinnerWheel />
        </Banner.Icon>
        <Banner.Title>
          The file analysis is in progress. Once completed, you'll be able to submit the prices.
        </Banner.Title>
      </Banner>
    )
  }

  if (rateFileAnalysisFail) {
    return (
      <Banner variant="danger" dismissible>
        <Banner.Title>
          We're sorry, the file analysis failed to complete. Please try importing the file again.
        </Banner.Title>
      </Banner>
    )
  }

  if (rateFileAnalysisSuccess) {
    return (
      <Banner variant="success" dismissible>
        <Banner.Title>
          Upload and file analysis complete! Remember to click 'Submit' or check 'Auto-Submit' to
          send your rates.
        </Banner.Title>
      </Banner>
    )
  }

  return null
}
